import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus, PencilSimple, Trash, UsersThree, Stack, SquaresFour, Plugs, Buildings, ArrowSquareOut, MagnifyingGlass, FunnelSimple, PaperPlaneRight, Sliders, X, Signature, Eye, Sparkle } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { uploadFile, fileDownloadUrl } from "@/lib/api";
import { TYPES, TYPE_META, SECTIONS, SECTION_META, ROLE_META, STATUS_META } from "@/lib/meta";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import IntegrationsPanel from "@/components/IntegrationsPanel";
import CompaniesPanel from "@/components/CompaniesPanel";
import ContactDrawer from "@/components/ContactDrawer";

const ROLES = ["lead", "client", "customer", "partner", "admin"];
const ACCESS_ROLES = ["lead", "client", "customer", "partner"];
const STATUSES = ["new", "prospect", "active", "churned"];

function Field({ label, children }) {
  return (
    <div className="space-y-1.5">
      <label className="font-mono text-[10px] tracking-[0.2em] uppercase text-muted-foreground">{label}</label>
      {children}
    </div>
  );
}

function AiGenerateButton({ data, patch }) {
  const [busy, setBusy] = useState(false);
  const run = async () => {
    if (!data.title) { toast.error("Add a title first"); return; }
    setBusy(true);
    try {
      const { data: res } = await api.post("/admin/ai/generate-content", {
        type: data.type, title: data.title, instruction: data.description, current: data.body || null,
      });
      patch({ body: res.body });
      toast.success(data.body ? "Content improved with Claude" : "Content drafted with Claude");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI generation failed");
    } finally { setBusy(false); }
  };
  return (
    <button type="button" data-testid="ai-generate-content" onClick={run} disabled={busy} className="inline-flex items-center gap-2 border border-primary/40 text-primary px-3 py-1.5 text-xs font-mono tracking-wide hover:bg-primary/10 transition-colors disabled:opacity-50">
      <Sparkle size={14} /> {busy ? "Writing…" : data.body ? "Improve with Claude" : "Generate with Claude"}
    </button>
  );
}

function ResourceFileField({ data, patch }) {  const [progress, setProgress] = useState(null);
  const isPlugin = data.type === "plugin";
  const accept = isPlugin ? ".zip" : ".pdf,.doc,.docx";
  const onPick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      setProgress(0);
      const rec = await uploadFile(file, setProgress);
      patch({ file_id: rec.id, file_name: rec.original_filename, url: "", ...(isPlugin ? { file_size: rec.size_human } : {}) });
      toast.success("File uploaded");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setProgress(null);
      e.target.value = "";
    }
  };
  return (
    <Field label={isPlugin ? "Plugin file (ZIP)" : "Attachment (PDF / DOC)"}>
      {data.file_id ? (
        <div className="flex items-center justify-between border border-border px-3 py-2" data-testid="res-uploaded-file">
          <span className="font-mono text-xs truncate">{data.file_name || "Uploaded file"}</span>
          <button type="button" data-testid="res-remove-file" onClick={() => patch({ file_id: null, file_name: null })} className="text-muted-foreground hover:text-primary shrink-0 ml-3"><X size={14} /></button>
        </div>
      ) : (
        <div>
          <input type="file" accept={accept} data-testid="res-file-input" onChange={onPick} disabled={progress !== null} className="block w-full text-xs font-mono file:mr-3 file:border file:border-border file:bg-secondary file:px-3 file:py-1.5 file:text-xs file:font-mono file:uppercase file:tracking-wide file:cursor-pointer" />
          {progress !== null && <div className="mt-2 h-1 bg-secondary"><div className="h-full bg-primary transition-all" style={{ width: `${progress}%` }} data-testid="res-upload-progress" /></div>}
        </div>
      )}
    </Field>
  );
}

const inputCls = "rounded-none bg-secondary border-border focus-visible:ring-primary";

export default function Admin() {
  const qc = useQueryClient();
  const [userDialog, setUserDialog] = useState(null);
  const [resDialog, setResDialog] = useState(null);
  const [svcDialog, setSvcDialog] = useState(null);
  const [drawer, setDrawer] = useState(null);

  const users = useQuery({ queryKey: ["admin-users"], queryFn: async () => (await api.get("/admin/users")).data });
  const resources = useQuery({ queryKey: ["admin-resources"], queryFn: async () => (await api.get("/resources")).data });
  const services = useQuery({ queryKey: ["admin-services"], queryFn: async () => (await api.get("/admin/services")).data });
  const companies = useQuery({ queryKey: ["companies"], queryFn: async () => (await api.get("/admin/companies")).data });
  const attributes = useQuery({ queryKey: ["attributes"], queryFn: async () => (await api.get("/admin/attributes")).data });
  const integrations = useQuery({ queryKey: ["integrations"], queryFn: async () => (await api.get("/admin/integrations")).data });
  const signatures = useQuery({ queryKey: ["signatures"], queryFn: async () => (await api.get("/admin/signatures")).data });
  const gmailReady = !!(integrations.data || []).find((i) => i.provider === "gmail")?.connected;

  const [filters, setFilters] = useState({ q: "", role: "all", status: "all", attrs: {} });
  const [selected, setSelected] = useState(new Set());
  const [bulk, setBulk] = useState(null);
  const [aiBulk, setAiBulk] = useState(null);
  const [attrMgr, setAttrMgr] = useState(false);
  const [attrForm, setAttrForm] = useState({ name: "", type: "text", options: "" });

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const g = params.get("gmail");
    if (g === "connected") toast.success("Google account connected");
    if (g === "error") toast.error("Google connection failed — check your keys and redirect URI");
    if (g) window.history.replaceState({}, "", window.location.pathname);
  }, []);

  const inval = () => {
    ["admin-users", "admin-resources", "admin-services", "resources", "services"].forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
  };

  const userMut = useMutation({
    mutationFn: async ({ mode, id, body }) => (mode === "create" ? api.post("/admin/users", body) : api.patch(`/admin/users/${id}`, body)),
    onSuccess: () => { inval(); setUserDialog(null); toast.success("User saved"); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Error"),
  });
  const userDel = useMutation({ mutationFn: async (id) => api.delete(`/admin/users/${id}`), onSuccess: () => { inval(); toast.success("User deleted"); } });

  const attrMut = useMutation({
    mutationFn: async (body) => api.post("/admin/attributes", body),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["attributes"] }); setAttrForm({ name: "", type: "text", options: "" }); toast.success("Field added"); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Error"),
  });
  const attrDel = useMutation({
    mutationFn: async (id) => api.delete(`/admin/attributes/${id}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["attributes"] }); toast.success("Field removed"); },
  });
  const bulkMut = useMutation({
    mutationFn: async (body) => api.post("/admin/gmail/send-bulk", body),
    onSuccess: (res) => { qc.invalidateQueries({ queryKey: ["admin-users"] }); setBulk(null); setSelected(new Set()); toast.success(`Sent ${res.data.sent}, failed ${res.data.failed}`); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Bulk send failed"),
  });
  const aiPreviewMut = useMutation({
    mutationFn: async (body) => api.post("/admin/gmail/draft-bulk-preview", body),
    onSuccess: (res) => { setAiBulk((prev) => ({ ...prev, stage: "review", drafts: res.data.drafts })); },
    onError: (e) => toast.error(e?.response?.data?.detail || "AI drafting failed"),
  });
  const aiSendDraftsMut = useMutation({
    mutationFn: async (body) => api.post("/admin/gmail/send-drafts", body),
    onSuccess: (res) => { qc.invalidateQueries({ queryKey: ["admin-users"] }); setAiBulk(null); setSelected(new Set()); toast.success(`Sent ${res.data.sent}, failed ${res.data.failed}`); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Send failed"),
  });

  const attrDefs = attributes.data || [];
  const selectAttrs = attrDefs.filter((a) => a.type === "select" || a.type === "multiselect");

  const matchAttr = (u) => Object.entries(filters.attrs).every(([key, val]) => {
    if (!val || val === "all") return true;
    const v = (u.attributes || {})[key];
    if (Array.isArray(v)) return v.includes(val);
    return v === val;
  });
  const filteredUsers = (users.data || []).filter((u) => {
    const q = filters.q.trim().toLowerCase();
    if (q && !`${u.name} ${u.email} ${u.company || ""}`.toLowerCase().includes(q)) return false;
    if (filters.role !== "all" && u.role !== filters.role) return false;
    if (filters.status !== "all" && u.status !== filters.status) return false;
    return matchAttr(u);
  });

  const allFilteredSelected = filteredUsers.length > 0 && filteredUsers.every((u) => selected.has(u.id));
  const toggleAll = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (allFilteredSelected) filteredUsers.forEach((u) => next.delete(u.id));
      else filteredUsers.forEach((u) => next.add(u.id));
      return next;
    });
  };
  const toggleOne = (id) => setSelected((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const resMut = useMutation({
    mutationFn: async ({ mode, id, body }) => (mode === "create" ? api.post("/admin/resources", body) : api.patch(`/admin/resources/${id}`, body)),
    onSuccess: () => { inval(); setResDialog(null); toast.success("Resource saved"); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Error"),
  });
  const resDel = useMutation({ mutationFn: async (id) => api.delete(`/admin/resources/${id}`), onSuccess: () => { inval(); toast.success("Resource deleted"); } });

  const svcMut = useMutation({
    mutationFn: async ({ mode, id, body }) => (mode === "create" ? api.post("/admin/services", body) : api.patch(`/admin/services/${id}`, body)),
    onSuccess: () => { inval(); setSvcDialog(null); toast.success("Service content saved"); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Error"),
  });
  const svcDel = useMutation({ mutationFn: async (id) => api.delete(`/admin/services/${id}`), onSuccess: () => { inval(); toast.success("Service content deleted"); } });

  const tabCls = "rounded-none px-6 py-3 data-[state=active]:bg-secondary data-[state=active]:text-primary font-mono text-xs tracking-wide";

  return (
    <div className="space-y-8" data-testid="admin-page">
      <div className="border-b border-border pb-6">
        <span className="font-mono text-[11px] tracking-[0.3em] uppercase text-primary">Admin · CMS / CRM</span>
        <h1 className="font-head text-3xl font-light tracking-tight mt-1">Manage portal</h1>
        <p className="text-sm text-muted-foreground font-light mt-1">Add and edit content, services and users manually - no AI credits used.</p>
      </div>

      <Tabs defaultValue="services">
        <TabsList className="rounded-none bg-card border border-border p-0 h-auto flex-wrap">
          <TabsTrigger value="services" data-testid="tab-services" className={tabCls}><SquaresFour size={16} className="mr-2" /> Services</TabsTrigger>
          <TabsTrigger value="resources" data-testid="tab-resources" className={tabCls}><Stack size={16} className="mr-2" /> Library</TabsTrigger>
          <TabsTrigger value="companies" data-testid="tab-companies" className={tabCls}><Buildings size={16} className="mr-2" /> Companies</TabsTrigger>
          <TabsTrigger value="users" data-testid="tab-users" className={tabCls}><UsersThree size={16} className="mr-2" /> Contacts (CRM)</TabsTrigger>
          <TabsTrigger value="signatures" data-testid="tab-signatures" className={tabCls}><Signature size={16} className="mr-2" /> Signatures</TabsTrigger>
          <TabsTrigger value="integrations" data-testid="tab-integrations" className={tabCls}><Plugs size={16} className="mr-2" /> Integrations</TabsTrigger>
        </TabsList>

        {/* SERVICES */}
        <TabsContent value="services" className="mt-6 space-y-4">
          <div className="flex justify-end">
            <Button onClick={() => setSvcDialog({ mode: "create", data: { section: "seo", allowed_roles: [], published: true, order: 0 } })} data-testid="add-service-button" className="rounded-none bg-primary hover:bg-primary/90 font-mono text-xs tracking-wide">
              <Plus size={16} className="mr-1" /> New service content
            </Button>
          </div>
          <div className="border border-border divide-y divide-border">
            {(services.data || []).map((s) => {
              const Icon = SECTION_META[s.section]?.icon || SquaresFour;
              return (
                <div key={s.id} className="flex items-center gap-4 p-4" data-testid={`admin-service-${s.id}`}>
                  <Icon size={20} className="text-muted-foreground shrink-0" />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium truncate">{s.title}</p>
                    <div className="flex flex-wrap gap-1 mt-1 items-center">
                      <span className="font-mono text-[10px] uppercase tracking-widest text-primary">{SECTION_META[s.section]?.label || s.section}</span>
                      {!s.published && <span className="font-mono text-[10px] text-amber-400">· draft</span>}
                      {s.allowed_roles?.map((ar) => <span key={ar} className="font-mono text-[10px] text-muted-foreground">· {ar}</span>)}
                    </div>
                  </div>
                  <button onClick={() => setSvcDialog({ mode: "edit", data: { ...s } })} data-testid={`edit-service-${s.id}`} className="text-muted-foreground hover:text-primary transition-colors p-1"><PencilSimple size={18} /></button>
                  <button onClick={() => svcDel.mutate(s.id)} data-testid={`delete-service-${s.id}`} className="text-muted-foreground hover:text-destructive transition-colors p-1"><Trash size={18} /></button>
                </div>
              );
            })}
          </div>
        </TabsContent>

        {/* RESOURCES */}
        <TabsContent value="resources" className="mt-6 space-y-4">
          <div className="flex justify-end">
            <Button onClick={() => setResDialog({ mode: "create", data: { type: "doc", allowed_roles: [] } })} data-testid="add-resource-button" className="rounded-none bg-primary hover:bg-primary/90 font-mono text-xs tracking-wide">
              <Plus size={16} className="mr-1" /> New resource
            </Button>
          </div>
          <div className="border border-border divide-y divide-border">
            {(resources.data || []).map((r) => {
              const Icon = TYPE_META[r.type]?.icon || Stack;
              return (
                <div key={r.id} className="flex items-center gap-4 p-4" data-testid={`admin-resource-${r.id}`}>
                  <Icon size={20} className="text-muted-foreground shrink-0" />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium truncate">{r.title}</p>
                    <div className="flex flex-wrap gap-1 mt-1">
                      <span className="font-mono text-[10px] uppercase tracking-widest text-primary">{r.type}</span>
                      {r.allowed_roles?.map((ar) => <span key={ar} className="font-mono text-[10px] text-muted-foreground">· {ar}</span>)}
                    </div>
                  </div>
                  <button onClick={() => setResDialog({ mode: "edit", data: { ...r } })} data-testid={`edit-resource-${r.id}`} className="text-muted-foreground hover:text-primary transition-colors p-1"><PencilSimple size={18} /></button>
                  <button onClick={() => resDel.mutate(r.id)} data-testid={`delete-resource-${r.id}`} className="text-muted-foreground hover:text-destructive transition-colors p-1"><Trash size={18} /></button>
                </div>
              );
            })}
          </div>
        </TabsContent>

        {/* COMPANIES */}
        <TabsContent value="companies" className="mt-6">
          <CompaniesPanel />
        </TabsContent>

        {/* SIGNATURES */}
        <TabsContent value="signatures" className="mt-6 space-y-4">
          <p className="text-sm text-muted-foreground font-light">Audit log of signed agreements. Click a row to view the captured signature.</p>
          <div className="border border-border divide-y divide-border" data-testid="signatures-audit">
            {(signatures.data || []).length === 0 ? (
              <p className="p-6 text-sm text-muted-foreground font-light" data-testid="signatures-empty">No agreements have been signed yet.</p>
            ) : (
              (signatures.data || []).map((s) => (
                <div key={s.id} className="flex items-center gap-4 p-4" data-testid={`signature-${s.id}`}>
                  <Signature size={20} className="text-primary shrink-0" />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium truncate">{s.resource_title}</p>
                    <div className="flex flex-wrap gap-1 mt-1 items-center">
                      <span className="font-mono text-[10px] tracking-widest uppercase text-muted-foreground">{s.user_name || s.user_email}</span>
                      <span className="font-mono text-[10px] text-muted-foreground">· {new Date(s.signed_at).toLocaleString()}</span>
                    </div>
                  </div>
                  <a href={fileDownloadUrl(s.file_id)} target="_blank" rel="noreferrer" data-testid={`view-signature-${s.id}`} className="flex items-center gap-1.5 border border-border px-3 py-1.5 text-xs font-mono tracking-wide hover:border-primary hover:text-primary transition-colors">
                    <Eye size={14} /> View
                  </a>
                </div>
              ))
            )}
          </div>
        </TabsContent>

        {/* USERS / CRM */}
        <TabsContent value="users" className="mt-6 space-y-4">
          {/* Filter bar */}
          <div className="flex flex-wrap items-center gap-2 border border-border p-3 bg-card">
            <div className="relative flex-1 min-w-[180px]">
              <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input data-testid="contact-search" className={`${inputCls} h-9 pl-9`} placeholder="Search name, email, company…" value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })} />
            </div>
            <Select value={filters.role} onValueChange={(v) => setFilters({ ...filters, role: v })}>
              <SelectTrigger data-testid="filter-role" className={`${inputCls} h-9 w-[130px]`}><FunnelSimple size={14} className="mr-1" /><SelectValue /></SelectTrigger>
              <SelectContent className="rounded-none"><SelectItem value="all" className="rounded-none">All roles</SelectItem>{ROLES.map((r) => <SelectItem key={r} value={r} className="rounded-none capitalize">{r}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={filters.status} onValueChange={(v) => setFilters({ ...filters, status: v })}>
              <SelectTrigger data-testid="filter-status" className={`${inputCls} h-9 w-[130px]`}><SelectValue /></SelectTrigger>
              <SelectContent className="rounded-none"><SelectItem value="all" className="rounded-none">All statuses</SelectItem>{STATUSES.map((s) => <SelectItem key={s} value={s} className="rounded-none capitalize">{s}</SelectItem>)}</SelectContent>
            </Select>
            {selectAttrs.map((a) => (
              <Select key={a.id} value={filters.attrs[a.key] || "all"} onValueChange={(v) => setFilters({ ...filters, attrs: { ...filters.attrs, [a.key]: v } })}>
                <SelectTrigger data-testid={`filter-attr-${a.key}`} className={`${inputCls} h-9 w-[150px]`}><SelectValue placeholder={a.name} /></SelectTrigger>
                <SelectContent className="rounded-none"><SelectItem value="all" className="rounded-none">All {a.name}</SelectItem>{a.options.map((o) => <SelectItem key={o} value={o} className="rounded-none">{o}</SelectItem>)}</SelectContent>
              </Select>
            ))}
            <Button onClick={() => setAttrMgr(true)} data-testid="manage-fields-button" className="rounded-none h-9 bg-secondary border border-border text-foreground hover:bg-accent font-mono text-xs"><Sliders size={14} className="mr-1" /> Fields</Button>
            <Button onClick={() => setUserDialog({ mode: "create", data: { role: "lead", status: "new", source: "manual", tags: [], attributes: {} } })} data-testid="add-user-button" className="rounded-none h-9 bg-primary hover:bg-primary/90 font-mono text-xs tracking-wide">
              <Plus size={16} className="mr-1" /> New
            </Button>
          </div>

          {/* Bulk action bar */}
          {selected.size > 0 && (
            <div className="flex items-center justify-between border border-primary/40 bg-primary/5 p-3" data-testid="bulk-bar">
              <span className="font-mono text-xs text-foreground">{selected.size} selected</span>
              <div className="flex items-center gap-2">
                <Button onClick={() => (gmailReady ? setAiBulk({ stage: "instruct", instruction: "", drafts: [] }) : toast.info("Connect Gmail in Integrations to send email."))} data-testid="ai-bulk-button" className="rounded-none h-9 border border-primary/40 bg-transparent text-primary hover:bg-primary/10 font-mono text-xs tracking-wide">
                  <Sparkle size={14} className="mr-1" /> AI draft &amp; send
                </Button>
                <Button onClick={() => (gmailReady ? setBulk({ subject: "", body: "" }) : toast.info("Connect Gmail in Integrations to send email."))} data-testid="bulk-email-button" className="rounded-none h-9 bg-primary hover:bg-primary/90 font-mono text-xs tracking-wide">
                  <PaperPlaneRight size={14} className="mr-1" /> Email {selected.size} contact{selected.size === 1 ? "" : "s"}
                </Button>
                <button onClick={() => setSelected(new Set())} data-testid="bulk-clear" className="text-muted-foreground hover:text-foreground p-1"><X size={16} /></button>
              </div>
            </div>
          )}

          {/* Table */}
          <div className="border border-border">
            <div className="flex items-center gap-3 p-3 border-b border-border bg-secondary/40">
              <input type="checkbox" data-testid="select-all" checked={allFilteredSelected} onChange={toggleAll} className="accent-[hsl(14_77%_44%)] w-4 h-4" />
              <span className="font-mono text-[10px] tracking-widest uppercase text-muted-foreground">{filteredUsers.length} contact{filteredUsers.length === 1 ? "" : "s"}</span>
            </div>
            <div className="divide-y divide-border">
              {filteredUsers.map((u) => {
                const role = ROLE_META[u.role] || ROLE_META.lead;
                const status = STATUS_META[u.status] || STATUS_META.active;
                return (
                  <div key={u.id} className="flex items-center gap-3 p-4" data-testid={`admin-user-${u.id}`}>
                    <input type="checkbox" data-testid={`select-${u.id}`} checked={selected.has(u.id)} onChange={() => toggleOne(u.id)} className="accent-[hsl(14_77%_44%)] w-4 h-4 shrink-0" />
                    <div className="min-w-0 flex-1 cursor-pointer" onClick={() => setDrawer(u)}>
                      <p className="text-sm font-medium truncate">{u.name}</p>
                      <p className="text-xs text-muted-foreground truncate font-mono">{u.email}{u.company ? ` · ${u.company}` : ""}{u.phone ? ` · ${u.phone}` : ""}</p>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {u.tags?.map((t) => <span key={t} className="font-mono text-[9px] text-muted-foreground border border-border px-1">{t}</span>)}
                        {selectAttrs.map((a) => (u.attributes?.[a.key] ? <span key={a.id} className="font-mono text-[9px] text-primary border border-primary/30 px-1">{a.name}: {Array.isArray(u.attributes[a.key]) ? u.attributes[a.key].join(", ") : u.attributes[a.key]}</span> : null))}
                      </div>
                    </div>
                    <span className={`px-2 py-0.5 border text-[10px] font-mono tracking-widest uppercase shrink-0 ${status.color}`}>{status.label}</span>
                    <span className={`px-2 py-0.5 border text-[10px] font-mono tracking-widest uppercase shrink-0 hidden sm:inline-block ${role.color}`}>{role.label}</span>
                    <button onClick={() => setDrawer(u)} data-testid={`contact-open-${u.id}`} className="text-muted-foreground hover:text-primary transition-colors p-1" title="Open contact"><ArrowSquareOut size={18} /></button>
                    <button onClick={() => setUserDialog({ mode: "edit", data: { ...u } })} data-testid={`edit-user-${u.id}`} className="text-muted-foreground hover:text-primary transition-colors p-1"><PencilSimple size={18} /></button>
                    <button onClick={() => { if (window.confirm(`Delete ${u.name}?`)) userDel.mutate(u.id); }} data-testid={`delete-user-${u.id}`} className="text-muted-foreground hover:text-destructive transition-colors p-1"><Trash size={18} /></button>
                  </div>
                );
              })}
              {filteredUsers.length === 0 && <p className="p-6 text-sm text-muted-foreground font-light" data-testid="contacts-empty">No contacts match your filters.</p>}
            </div>
          </div>
        </TabsContent>

        {/* INTEGRATIONS */}
        <TabsContent value="integrations" className="mt-6">
          <IntegrationsPanel />
        </TabsContent>
      </Tabs>

      {/* SERVICE DIALOG */}
      <Dialog open={!!svcDialog} onOpenChange={(o) => !o && setSvcDialog(null)}>
        <DialogContent className="rounded-none border-border bg-card max-w-lg max-h-[88vh] overflow-y-auto" data-testid="service-dialog">
          <DialogHeader><DialogTitle className="font-head text-2xl font-light">{svcDialog?.mode === "create" ? "New service content" : "Edit service content"}</DialogTitle><DialogDescription className="sr-only">Manage service section content.</DialogDescription></DialogHeader>
          {svcDialog && (
            <div className="space-y-4">
              <Field label="Section">
                <Select value={svcDialog.data.section} onValueChange={(v) => setSvcDialog({ ...svcDialog, data: { ...svcDialog.data, section: v } })}>
                  <SelectTrigger data-testid="svc-section-select" className={inputCls}><SelectValue /></SelectTrigger>
                  <SelectContent className="rounded-none">{SECTIONS.map((s) => <SelectItem key={s} value={s} className="rounded-none">{SECTION_META[s].label}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label="Title"><Input data-testid="svc-title-input" className={inputCls} value={svcDialog.data.title || ""} onChange={(e) => setSvcDialog({ ...svcDialog, data: { ...svcDialog.data, title: e.target.value } })} /></Field>
              <Field label="Subtitle"><Input data-testid="svc-subtitle-input" className={inputCls} value={svcDialog.data.subtitle || ""} onChange={(e) => setSvcDialog({ ...svcDialog, data: { ...svcDialog.data, subtitle: e.target.value } })} /></Field>
              <Field label="Short description"><Input data-testid="svc-desc-input" className={inputCls} value={svcDialog.data.description || ""} onChange={(e) => setSvcDialog({ ...svcDialog, data: { ...svcDialog.data, description: e.target.value } })} /></Field>
              <Field label="Body (markdown)"><Textarea data-testid="svc-body-input" rows={5} className={`${inputCls} font-mono text-xs`} value={svcDialog.data.body || ""} onChange={(e) => setSvcDialog({ ...svcDialog, data: { ...svcDialog.data, body: e.target.value } })} /></Field>
              <Field label="Image URL"><Input data-testid="svc-image-input" className={inputCls} value={svcDialog.data.image_url || ""} onChange={(e) => setSvcDialog({ ...svcDialog, data: { ...svcDialog.data, image_url: e.target.value } })} /></Field>
              <div className="grid grid-cols-2 gap-4">
                <Field label="CTA label"><Input data-testid="svc-cta-label-input" className={inputCls} value={svcDialog.data.cta_label || ""} onChange={(e) => setSvcDialog({ ...svcDialog, data: { ...svcDialog.data, cta_label: e.target.value } })} /></Field>
                <Field label="Order"><Input data-testid="svc-order-input" type="number" className={inputCls} value={svcDialog.data.order ?? 0} onChange={(e) => setSvcDialog({ ...svcDialog, data: { ...svcDialog.data, order: parseInt(e.target.value) || 0 } })} /></Field>
              </div>
              <Field label="CTA URL"><Input data-testid="svc-cta-url-input" className={inputCls} value={svcDialog.data.cta_url || ""} onChange={(e) => setSvcDialog({ ...svcDialog, data: { ...svcDialog.data, cta_url: e.target.value } })} /></Field>
              <Field label="Access roles">
                <div className="flex flex-wrap gap-2" data-testid="svc-roles">
                  {ACCESS_ROLES.map((r) => {
                    const active = (svcDialog.data.allowed_roles || []).includes(r);
                    return <button key={r} type="button" data-testid={`svc-role-${r}`} onClick={() => { const cur = svcDialog.data.allowed_roles || []; setSvcDialog({ ...svcDialog, data: { ...svcDialog.data, allowed_roles: active ? cur.filter((x) => x !== r) : [...cur, r] } }); }} className={`px-3 py-1.5 border font-mono text-[11px] tracking-widest uppercase transition-colors ${active ? "border-primary text-primary bg-primary/10" : "border-border text-muted-foreground hover:text-foreground"}`}>{r}</button>;
                  })}
                </div>
              </Field>
              <div className="flex items-center gap-3">
                <Switch data-testid="svc-published-switch" checked={!!svcDialog.data.published} onCheckedChange={(v) => setSvcDialog({ ...svcDialog, data: { ...svcDialog.data, published: v } })} />
                <span className="font-mono text-xs text-muted-foreground">{svcDialog.data.published ? "Published" : "Draft"}</span>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button data-testid="save-service-button" className="rounded-none bg-primary hover:bg-primary/90 font-mono text-xs tracking-wide" onClick={() => {
              const d = svcDialog.data;
              const body = { section: d.section, title: d.title, subtitle: d.subtitle, description: d.description, body: d.body, image_url: d.image_url, cta_label: d.cta_label, cta_url: d.cta_url, order: d.order || 0, allowed_roles: d.allowed_roles || [], published: !!d.published };
              svcMut.mutate({ mode: svcDialog.mode, id: d.id, body });
            }}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* USER DIALOG */}
      <Dialog open={!!userDialog} onOpenChange={(o) => !o && setUserDialog(null)}>
        <DialogContent className="rounded-none border-border bg-card max-w-lg max-h-[88vh] overflow-y-auto" data-testid="user-dialog">
          <DialogHeader><DialogTitle className="font-head text-2xl font-light">{userDialog?.mode === "create" ? "New contact" : "Edit contact"}</DialogTitle><DialogDescription className="sr-only">Create or edit a contact and their details.</DialogDescription></DialogHeader>
          {userDialog && (
            <div className="space-y-4">
              <Field label="Name"><Input data-testid="user-name-input" className={inputCls} value={userDialog.data.name || ""} onChange={(e) => setUserDialog({ ...userDialog, data: { ...userDialog.data, name: e.target.value } })} /></Field>
              <Field label="Email"><Input data-testid="user-email-input" type="email" disabled={userDialog.mode === "edit"} className={`${inputCls} disabled:opacity-60`} value={userDialog.data.email || ""} onChange={(e) => setUserDialog({ ...userDialog, data: { ...userDialog.data, email: e.target.value } })} /></Field>
              <div className="grid grid-cols-2 gap-4">
                <Field label="Company"><Input data-testid="user-company-input" className={inputCls} value={userDialog.data.company || ""} onChange={(e) => setUserDialog({ ...userDialog, data: { ...userDialog.data, company: e.target.value } })} /></Field>
                <Field label="Phone"><Input data-testid="user-phone-input" className={inputCls} value={userDialog.data.phone || ""} onChange={(e) => setUserDialog({ ...userDialog, data: { ...userDialog.data, phone: e.target.value } })} /></Field>
              </div>
              <Field label="Link to existing company (optional)">
                <Select value={userDialog.data.company_id || ""} onValueChange={(v) => { const c = (companies.data || []).find((x) => x.id === v); setUserDialog({ ...userDialog, data: { ...userDialog.data, company_id: v, company: c ? c.name : userDialog.data.company } }); }}>
                  <SelectTrigger data-testid="user-company-select" className={inputCls}><SelectValue placeholder="Choose a company…" /></SelectTrigger>
                  <SelectContent className="rounded-none">
                    {(companies.data || []).map((c) => <SelectItem key={c.id} value={c.id} className="rounded-none">{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
              <div className="grid grid-cols-2 gap-4">
                <Field label="Role">
                  <Select value={userDialog.data.role} onValueChange={(v) => setUserDialog({ ...userDialog, data: { ...userDialog.data, role: v } })}>
                    <SelectTrigger data-testid="user-role-select" className={inputCls}><SelectValue /></SelectTrigger>
                    <SelectContent className="rounded-none">{ROLES.map((r) => <SelectItem key={r} value={r} className="rounded-none capitalize">{r}</SelectItem>)}</SelectContent>
                  </Select>
                </Field>
                <Field label="Status">
                  <Select value={userDialog.data.status || "active"} onValueChange={(v) => setUserDialog({ ...userDialog, data: { ...userDialog.data, status: v } })}>
                    <SelectTrigger data-testid="user-status-select" className={inputCls}><SelectValue /></SelectTrigger>
                    <SelectContent className="rounded-none">{STATUSES.map((s) => <SelectItem key={s} value={s} className="rounded-none capitalize">{s}</SelectItem>)}</SelectContent>
                  </Select>
                </Field>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <Field label="Source"><Input data-testid="user-source-input" className={inputCls} placeholder="manual / woocommerce / hubspot" value={userDialog.data.source || ""} onChange={(e) => setUserDialog({ ...userDialog, data: { ...userDialog.data, source: e.target.value } })} /></Field>
                <Field label="Tags (comma separated)"><Input data-testid="user-tags-input" className={inputCls} value={(userDialog.data.tags || []).join(", ")} onChange={(e) => setUserDialog({ ...userDialog, data: { ...userDialog.data, tags: e.target.value.split(",").map((t) => t.trim()).filter(Boolean) } })} /></Field>
              </div>
              <Field label="Notes"><Textarea data-testid="user-notes-input" rows={3} className={inputCls} value={userDialog.data.notes || ""} onChange={(e) => setUserDialog({ ...userDialog, data: { ...userDialog.data, notes: e.target.value } })} /></Field>
              <div className="border-t border-border pt-4 space-y-3">
                <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-muted-foreground">3LA report card (0–100)</p>
                <div className="grid grid-cols-3 gap-3">
                  {[["l1", "L1 Human"], ["l2", "L2 Search"], ["l3", "L3 AI"]].map(([k, lbl]) => (
                    <Field key={k} label={lbl}>
                      <Input data-testid={`tla-${k}-input`} type="number" min="0" max="100" className={inputCls} value={userDialog.data.tla?.[k] ?? ""} onChange={(e) => setUserDialog({ ...userDialog, data: { ...userDialog.data, tla: { ...(userDialog.data.tla || {}), [k]: e.target.value === "" ? null : Number(e.target.value) } } })} />
                    </Field>
                  ))}
                </div>
                <Field label="Report note (optional)"><Input data-testid="tla-note-input" className={inputCls} value={userDialog.data.tla?.note || ""} onChange={(e) => setUserDialog({ ...userDialog, data: { ...userDialog.data, tla: { ...(userDialog.data.tla || {}), note: e.target.value } } })} /></Field>
              </div>
              {attrDefs.length > 0 && (
                <div className="border-t border-border pt-4 space-y-4">
                  <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-muted-foreground">Custom fields</p>
                  {attrDefs.map((a) => {
                    const val = (userDialog.data.attributes || {})[a.key];
                    const setVal = (v) => setUserDialog({ ...userDialog, data: { ...userDialog.data, attributes: { ...(userDialog.data.attributes || {}), [a.key]: v } } });
                    return (
                      <Field key={a.id} label={a.name}>
                        {a.type === "text" && <Input data-testid={`attr-input-${a.key}`} className={inputCls} value={val || ""} onChange={(e) => setVal(e.target.value)} />}
                        {a.type === "number" && <Input data-testid={`attr-input-${a.key}`} type="number" className={inputCls} value={val ?? ""} onChange={(e) => setVal(e.target.value)} />}
                        {a.type === "date" && <Input data-testid={`attr-input-${a.key}`} type="date" className={inputCls} value={val || ""} onChange={(e) => setVal(e.target.value)} />}
                        {a.type === "select" && (
                          <Select value={val || ""} onValueChange={setVal}>
                            <SelectTrigger data-testid={`attr-input-${a.key}`} className={inputCls}><SelectValue placeholder="Choose…" /></SelectTrigger>
                            <SelectContent className="rounded-none">{a.options.map((o) => <SelectItem key={o} value={o} className="rounded-none">{o}</SelectItem>)}</SelectContent>
                          </Select>
                        )}
                        {a.type === "multiselect" && (
                          <div className="flex flex-wrap gap-2" data-testid={`attr-input-${a.key}`}>
                            {a.options.map((o) => {
                              const arr = Array.isArray(val) ? val : [];
                              const active = arr.includes(o);
                              return <button key={o} type="button" onClick={() => setVal(active ? arr.filter((x) => x !== o) : [...arr, o])} className={`px-3 py-1.5 border font-mono text-[11px] tracking-wide transition-colors ${active ? "border-primary text-primary bg-primary/10" : "border-border text-muted-foreground hover:text-foreground"}`}>{o}</button>;
                            })}
                          </div>
                        )}
                      </Field>
                    );
                  })}
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button data-testid="save-user-button" className="rounded-none bg-primary hover:bg-primary/90 font-mono text-xs tracking-wide" onClick={() => {
              const d = userDialog.data;
              const common = { name: d.name, role: d.role, company: d.company, company_id: d.company_id, phone: d.phone, status: d.status, source: d.source, tags: d.tags || [], notes: d.notes, attributes: d.attributes || {}, ...(d.tla ? { tla: d.tla } : {}) };
              const body = userDialog.mode === "create" ? { email: d.email, ...common } : common;
              userMut.mutate({ mode: userDialog.mode, id: d.id, body });
            }}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* RESOURCE DIALOG */}
      <Dialog open={!!resDialog} onOpenChange={(o) => !o && setResDialog(null)}>
        <DialogContent className="rounded-none border-border bg-card max-w-lg max-h-[88vh] overflow-y-auto" data-testid="resource-dialog">
          <DialogHeader><DialogTitle className="font-head text-2xl font-light">{resDialog?.mode === "create" ? "New resource" : "Edit resource"}</DialogTitle><DialogDescription className="sr-only">Create or edit a library resource.</DialogDescription></DialogHeader>
          {resDialog && (
            <div className="space-y-4">
              <Field label="Title"><Input data-testid="res-title-input" className={inputCls} value={resDialog.data.title || ""} onChange={(e) => setResDialog({ ...resDialog, data: { ...resDialog.data, title: e.target.value } })} /></Field>
              <Field label="Description"><Input data-testid="res-desc-input" className={inputCls} value={resDialog.data.description || ""} onChange={(e) => setResDialog({ ...resDialog, data: { ...resDialog.data, description: e.target.value } })} /></Field>
              <div className="grid grid-cols-2 gap-4">
                <Field label="Type">
                  <Select value={resDialog.data.type} onValueChange={(v) => setResDialog({ ...resDialog, data: { ...resDialog.data, type: v } })}>
                    <SelectTrigger data-testid="res-type-select" className={inputCls}><SelectValue /></SelectTrigger>
                    <SelectContent className="rounded-none">{TYPES.map((t) => <SelectItem key={t} value={t} className="rounded-none capitalize">{t}</SelectItem>)}</SelectContent>
                  </Select>
                </Field>
                <Field label="Category"><Input data-testid="res-category-input" className={inputCls} value={resDialog.data.category || ""} onChange={(e) => setResDialog({ ...resDialog, data: { ...resDialog.data, category: e.target.value } })} /></Field>
              </div>
              <Field label="Access roles">
                <div className="flex flex-wrap gap-2" data-testid="res-roles">
                  {ACCESS_ROLES.map((r) => {
                    const active = (resDialog.data.allowed_roles || []).includes(r);
                    return <button key={r} type="button" data-testid={`res-role-${r}`} onClick={() => { const cur = resDialog.data.allowed_roles || []; setResDialog({ ...resDialog, data: { ...resDialog.data, allowed_roles: active ? cur.filter((x) => x !== r) : [...cur, r] } }); }} className={`px-3 py-1.5 border font-mono text-[11px] tracking-widest uppercase transition-colors ${active ? "border-primary text-primary bg-primary/10" : "border-border text-muted-foreground hover:text-foreground"}`}>{r}</button>;
                  })}
                </div>
              </Field>
              {resDialog.data.type === "plugin" && (
                <div className="grid grid-cols-2 gap-4">
                  <Field label="Version"><Input data-testid="res-version-input" className={inputCls} value={resDialog.data.version || ""} onChange={(e) => setResDialog({ ...resDialog, data: { ...resDialog.data, version: e.target.value } })} /></Field>
                  <Field label="File size"><Input data-testid="res-size-input" className={inputCls} value={resDialog.data.file_size || ""} onChange={(e) => setResDialog({ ...resDialog, data: { ...resDialog.data, file_size: e.target.value } })} /></Field>
                </div>
              )}
              <ResourceFileField data={resDialog.data} patch={(p) => setResDialog({ ...resDialog, data: { ...resDialog.data, ...p } })} />
              <Field label={resDialog.data.type === "plugin" ? "External download URL (optional)" : "External URL (optional)"}><Input data-testid="res-url-input" className={inputCls} value={resDialog.data.url || ""} onChange={(e) => setResDialog({ ...resDialog, data: { ...resDialog.data, url: e.target.value } })} /></Field>
              {resDialog.data.type !== "plugin" && (
                <div className="space-y-2">
                  <Field label="Content (markdown)"><Textarea data-testid="res-body-input" rows={6} className={`${inputCls} font-mono text-xs`} value={resDialog.data.body || ""} onChange={(e) => setResDialog({ ...resDialog, data: { ...resDialog.data, body: e.target.value } })} /></Field>
                  <AiGenerateButton data={resDialog.data} patch={(p) => setResDialog({ ...resDialog, data: { ...resDialog.data, ...p } })} />
                </div>
              )}
              {resDialog.data.type === "agreement" && (
                <div className="flex items-center justify-between border border-border px-4 py-3">
                  <div>
                    <p className="text-sm font-medium">Requires signature</p>
                    <p className="text-xs text-muted-foreground font-light">Users must draw a signature to accept this agreement.</p>
                  </div>
                  <Switch data-testid="res-requires-signature" checked={!!resDialog.data.requires_signature} onCheckedChange={(v) => setResDialog({ ...resDialog, data: { ...resDialog.data, requires_signature: v } })} />
                </div>
              )}
            </div>
          )}
          <DialogFooter className="gap-2 sm:justify-between">
            {resDialog?.mode === "edit" && resDialog?.data?.type === "plugin" ? (
              <Button
                type="button"
                variant="outline"
                data-testid="notify-update-button"
                className="rounded-none border-primary text-primary hover:bg-primary/10 font-mono text-xs tracking-wide"
                onClick={async () => {
                  try {
                    const { data } = await api.post(`/admin/resources/${resDialog.data.id}/notify-update`, { portal_base: window.location.origin });
                    toast.success(`Update email sent to ${data.delivered}/${data.recipients} contact${data.recipients === 1 ? "" : "s"}`);
                  } catch (err) {
                    toast.error(err?.response?.data?.detail || "Failed to send notifications");
                  }
                }}
              >
                <PaperPlaneRight size={14} className="mr-1.5" /> Notify users of update
              </Button>
            ) : <span />}
            <Button data-testid="save-resource-button" className="rounded-none bg-primary hover:bg-primary/90 font-mono text-xs tracking-wide" onClick={() => {
              const d = resDialog.data;
              const body = { title: d.title, description: d.description, type: d.type, category: d.category, allowed_roles: d.allowed_roles || [], url: d.url, file_id: d.file_id, file_name: d.file_name, body: d.body, version: d.version, file_size: d.file_size, requires_signature: !!d.requires_signature };
              resMut.mutate({ mode: resDialog.mode, id: d.id, body });
            }}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ContactDrawer
        contact={drawer}
        company={drawer ? (companies.data || []).find((c) => c.id === drawer.company_id) : null}
        open={!!drawer}
        onClose={() => setDrawer(null)}
        emailReady={gmailReady}
        onEmail={() => toast.info("Connect your Google Workspace in the Integrations tab to send email from your own domain.")}
      />

      {/* ATTRIBUTES MANAGER */}
      <Dialog open={attrMgr} onOpenChange={setAttrMgr}>
        <DialogContent className="rounded-none border-border bg-card max-w-lg max-h-[88vh] overflow-y-auto" data-testid="attributes-dialog">
          <DialogHeader><DialogTitle className="font-head text-2xl font-normal">Custom fields</DialogTitle><DialogDescription className="sr-only">Manage custom contact attributes.</DialogDescription></DialogHeader>
          <div className="space-y-2">
            {attrDefs.map((a) => (
              <div key={a.id} className="flex items-center justify-between border border-border p-3" data-testid={`attr-row-${a.key}`}>
                <div>
                  <p className="text-sm font-medium">{a.name}</p>
                  <p className="font-mono text-[10px] text-muted-foreground uppercase tracking-widest">{a.type}{a.options.length ? ` · ${a.options.join(", ")}` : ""}</p>
                </div>
                <button onClick={() => { if (window.confirm(`Remove field "${a.name}"?`)) attrDel.mutate(a.id); }} data-testid={`delete-attr-${a.key}`} className="text-muted-foreground hover:text-destructive transition-colors p-1"><Trash size={16} /></button>
              </div>
            ))}
            {attrDefs.length === 0 && <p className="text-sm text-muted-foreground font-light">No custom fields yet.</p>}
          </div>
          <div className="border-t border-border pt-4 space-y-3">
            <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-muted-foreground">Add a field</p>
            <div className="grid grid-cols-2 gap-3">
              <Input data-testid="attr-name-input" className={inputCls} placeholder="Field name" value={attrForm.name} onChange={(e) => setAttrForm({ ...attrForm, name: e.target.value })} />
              <Select value={attrForm.type} onValueChange={(v) => setAttrForm({ ...attrForm, type: v })}>
                <SelectTrigger data-testid="attr-type-select" className={inputCls}><SelectValue /></SelectTrigger>
                <SelectContent className="rounded-none">{["text", "number", "date", "select", "multiselect"].map((t) => <SelectItem key={t} value={t} className="rounded-none capitalize">{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {(attrForm.type === "select" || attrForm.type === "multiselect") && (
              <Input data-testid="attr-options-input" className={inputCls} placeholder="Options, comma separated" value={attrForm.options} onChange={(e) => setAttrForm({ ...attrForm, options: e.target.value })} />
            )}
            <Button
              data-testid="add-attribute-button"
              disabled={!attrForm.name || attrMut.isPending}
              onClick={() => attrMut.mutate({ name: attrForm.name, type: attrForm.type, options: attrForm.options.split(",").map((o) => o.trim()).filter(Boolean) })}
              className="rounded-none bg-primary hover:bg-primary/90 font-mono text-xs tracking-wide"
            >Add field</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* BULK EMAIL */}
      <Dialog open={!!bulk} onOpenChange={(o) => !o && setBulk(null)}>
        <DialogContent className="rounded-none border-border bg-card max-w-xl" data-testid="bulk-dialog">
          <DialogHeader>
            <span className="font-mono text-[10px] tracking-[0.3em] uppercase text-primary text-left">Bulk email</span>
            <DialogTitle className="font-head text-2xl font-normal text-left">To {selected.size} contact{selected.size === 1 ? "" : "s"}</DialogTitle>
            <DialogDescription className="sr-only">Compose a bulk email to the selected contacts.</DialogDescription>
          </DialogHeader>
          {bulk && (
            <div className="space-y-3">
              <Input data-testid="bulk-subject" className={inputCls} placeholder="Subject" value={bulk.subject} onChange={(e) => setBulk({ ...bulk, subject: e.target.value })} />
              <Textarea data-testid="bulk-body" rows={8} className={inputCls} placeholder="Hi {{first_name}}, …" value={bulk.body} onChange={(e) => setBulk({ ...bulk, body: e.target.value })} />
              <p className="font-mono text-[10px] text-muted-foreground leading-relaxed">
                Variables: <span className="text-foreground">{"{{name}} {{first_name}} {{company}} {{email}}"}</span>
                {attrDefs.length > 0 && <> · {attrDefs.map((a) => `{{${a.key}}}`).join(" ")}</>}
              </p>
            </div>
          )}
          <DialogFooter>
            <Button
              data-testid="bulk-send"
              disabled={!bulk?.subject || !bulk?.body || bulkMut.isPending}
              onClick={() => bulkMut.mutate({ contact_ids: [...selected], subject: bulk.subject, body: bulk.body })}
              className="rounded-none bg-primary hover:bg-primary/90 font-mono text-xs tracking-wide"
            ><PaperPlaneRight size={16} className="mr-2" /> {bulkMut.isPending ? "Sending…" : `Send to ${selected.size}`}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* AI DRAFT & SEND DIALOG */}
      <Dialog open={!!aiBulk} onOpenChange={(o) => !o && setAiBulk(null)}>
        <DialogContent className="rounded-none border-border bg-card max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="ai-bulk-dialog">
          <DialogHeader>
            <span className="font-mono text-[10px] tracking-[0.3em] uppercase text-primary text-left">Claude · AI campaign</span>
            <DialogTitle className="font-head text-2xl font-normal text-left">
              {aiBulk?.stage === "review" ? `Review ${aiBulk.drafts.length} draft${aiBulk.drafts.length === 1 ? "" : "s"}` : `Draft & send to ${selected.size} contact${selected.size === 1 ? "" : "s"}`}
            </DialogTitle>
            <DialogDescription className="sr-only">Claude personalizes an email for each selected contact; preview and edit before sending.</DialogDescription>
          </DialogHeader>

          {aiBulk?.stage === "instruct" && (
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground font-light">
                Claude writes a unique email for each contact using their profile, 3LA scores and recent activity. Give it a goal — you'll preview every draft before anything sends.
              </p>
              <Textarea data-testid="ai-bulk-instruction" rows={4} className={inputCls} placeholder="e.g. Invite them to review their Q3 SEO report and book a 20-min call" value={aiBulk.instruction} onChange={(e) => setAiBulk({ ...aiBulk, instruction: e.target.value })} />
              <p className="font-mono text-[10px] text-muted-foreground">Generating {selected.size} draft{selected.size === 1 ? "" : "s"} may take a moment.</p>
            </div>
          )}

          {aiBulk?.stage === "review" && (
            <div className="space-y-4" data-testid="ai-bulk-review">
              <p className="text-sm text-muted-foreground font-light">Spot-check and edit any draft, then send all. Nothing has been sent yet.</p>
              {aiBulk.drafts.map((d, i) => (
                <div key={d.contact_id} className="border border-border p-3 space-y-2" data-testid={`ai-draft-${i}`}>
                  <div className="flex items-center justify-between">
                    <span className="font-mono text-[11px] tracking-wide text-primary">{d.name || d.email}</span>
                    <span className="font-mono text-[10px] text-muted-foreground">{d.email}</span>
                  </div>
                  <Input data-testid={`ai-draft-subject-${i}`} className={inputCls} value={d.subject} onChange={(e) => setAiBulk((p) => { const drafts = [...p.drafts]; drafts[i] = { ...drafts[i], subject: e.target.value }; return { ...p, drafts }; })} />
                  <Textarea data-testid={`ai-draft-body-${i}`} rows={5} className={`${inputCls} text-xs`} value={d.body} onChange={(e) => setAiBulk((p) => { const drafts = [...p.drafts]; drafts[i] = { ...drafts[i], body: e.target.value }; return { ...p, drafts }; })} />
                </div>
              ))}
            </div>
          )}

          <DialogFooter>
            {aiBulk?.stage === "instruct" && (
              <Button
                data-testid="ai-bulk-generate"
                disabled={aiPreviewMut.isPending}
                onClick={() => aiPreviewMut.mutate({ contact_ids: [...selected], instruction: aiBulk.instruction })}
                className="rounded-none bg-primary hover:bg-primary/90 font-mono text-xs tracking-wide"
              ><Sparkle size={16} className="mr-2" /> {aiPreviewMut.isPending ? "Drafting…" : `Generate ${selected.size} draft${selected.size === 1 ? "" : "s"}`}</Button>
            )}
            {aiBulk?.stage === "review" && (
              <div className="flex items-center justify-between w-full gap-2">
                <Button variant="ghost" data-testid="ai-bulk-back" onClick={() => setAiBulk({ ...aiBulk, stage: "instruct" })} className="rounded-none font-mono text-xs tracking-wide">← Redraft</Button>
                <Button
                  data-testid="ai-bulk-send-all"
                  disabled={aiSendDraftsMut.isPending || aiBulk.drafts.length === 0}
                  onClick={() => aiSendDraftsMut.mutate({ drafts: aiBulk.drafts })}
                  className="rounded-none bg-primary hover:bg-primary/90 font-mono text-xs tracking-wide"
                ><PaperPlaneRight size={16} className="mr-2" /> {aiSendDraftsMut.isPending ? "Sending…" : `Send all ${aiBulk.drafts.length}`}</Button>
              </div>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
